(ns nosco.ui.ai.chat-parts
  "Presentation-only rendering for canonical Vercel UIMessage parts."
  (:require
   ["ai" :refer [getToolName isToolUIPart]]
   ["react" :as React]
   [clojure.string :as string]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.ai.context-budget :as context-budget]
   [nosco.ai.tool-catalogue :as tool-catalogue]
   [nosco.ai.vercel-chat :as vercel-chat]
   [nosco.ai.vercel-tool-bridge :as tool-bridge]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons2]
   [nosco.ui.animations :as animations]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.checkbox :as checkbox]
   [nosco.ui.html :as ui-html]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.menus :as menus]
   [nosco.ui.progress :as progress]
   [nosco.ui.text :as text]
   [nosco.util.format :as nosco-format]
   [nosco.util.markdown :as markdown]))

(defn- nonblank [value]
  (when (and (string? value) (not (string/blank? value))) value))

(defn- payload->clj [value]
  (when-not (nil? value)
    (js->clj value :keywordize-keys true)))

(defn- payload->text [value]
  (cond
    (nil? value) nil
    (string? value) value
    :else
    (try
      (.stringify js/JSON (clj->js (payload->clj value)) nil 2)
      (catch :default _
        (pr-str (payload->clj value))))))

(defn- run-action! [set-submitting! set-error! action!]
  (set-submitting! true)
  (set-error! nil)
  (try
    (-> (js/Promise.resolve (action!))
        (.then (fn [accepted?]
                 (set-submitting! false)
                 (when (false? accepted?)
                   (set-error! (t/t "This interaction is no longer available.")))))
        (.catch (fn [error]
                  (set-submitting! false)
                  (set-error! (t/t "The interaction could not be completed."))
                  (when-not (= "AbortError" (.-name error))
                    (js/console.error "AI Chat interaction failed" error)))))
    (catch :default error
      (set-submitting! false)
      (set-error! (t/t "The interaction could not be completed."))
      (js/console.error "AI Chat interaction failed" error))))

(def ^:private markdown-base-class
  ["nosco-chat-msg__markdown"
   "nosco-redactor-text"
   "nosco-redactor-text--document-compact"])

(defnc CommittedMarkdownBlock
  "A completed streaming block. React.memo keeps its parsed and sanitized DOM
   stable while later blocks continue streaming."
  [{:keys [content]}]
  {:wrap [(React/memo)]}
  (when-let [html (markdown/prepare-streaming-ai-markdown content false)]
    [ui-html/SafeHTMLStrict
     {:html html
      :class "nosco-chat-msg__markdown-block"}]))

(defnc MarkdownBody
  "Assistant Markdown. While streaming, completed blocks are memoized React
   children and only the active tail is re-parsed per chunk. The streaming
   output stays one flex item, like the settled single container, so the
   message's flex gap never opens between committed and active blocks."
  [{:keys [content streaming?]}]
  (let [content (or content "")]
    (if streaming?
      (let [{:keys [blocks active]} (markdown/split-streaming-blocks content)
            active-html (markdown/prepare-streaming-ai-markdown active true)]
        [:div
         (when (seq blocks)
           [:div {:key "committed"
                  :class markdown-base-class}
            (map-indexed (fn [index block]
                           [CommittedMarkdownBlock {:key index :content block}])
                         blocks)])
         (when active-html
           [ui-html/SafeHTMLStrict
            {:key (str "active-" (count blocks))
             :html active-html
             :class (conj markdown-base-class
                          "nosco-chat-msg__markdown--activity-slot"
                          "nosco-chat-msg__markdown--streaming"
                          "nosco-chat-msg__markdown--reveal")}])
         [:span {:key "status" :class "sr-only" :role "status"} (t/t "AI is working")]])
      (when-let [html (markdown/prepare-streaming-ai-markdown content false)]
        [ui-html/SafeHTMLStrict
         {:html html
          :class (conj markdown-base-class "nosco-chat-msg__markdown--activity-slot")}]))))

(defnc ActivityIndicator [{:keys [label]}]
  [:div {:class "nosco-chat-progress" :role "status"
         :aria-label (or label (t/t "AI is working"))}
   [:span {:class "nosco-chat-pending-dot" :aria-hidden true}]
   (when label
     [text/Text {:el :span :variant text/Body-Sm-Regular} label])])

(defnc ToolRunningIndicator []
  [:span {:class "nosco-chat-tool-running-indicator" :aria-hidden true}])

(defn- activity-label
  "The one-line label for a tool step in `phase`."
  [{:keys [phase description completed-description proposed-description]}]
  (case phase
    :preparing (or (nonblank description) (t/t "Working…"))
    :running (or (nonblank description) (t/t "Working…"))
    :succeeded (or (nonblank completed-description) (t/t "Completed"))
    :failed (t/t "Error: %s" (or (nonblank description)
                                 (t/t "Working…")))
    :rejected (or (nonblank proposed-description) (t/t "Proposed action"))
    :deferred (t/t "Deferred until the conversation was summarised: %s"
                   (or (nonblank description) (t/t "Working…")))
    :postponed (t/t "Postponed to the next step: %s"
                    (or (nonblank description) (t/t "Working…")))))

(defnc ToolActivity
  "Compact presentation for a read or write tool phase. `phase` records the real
   lifecycle distinction even where preparing and running intentionally share
   one visual treatment. `tokens` is the estimated size of the result the model
   received, shown in the details."
  [{:keys [phase description details tokens] :as props}]
  (let [generic-working? (and (= :preparing phase) (nil? (nonblank description)))
        definition-streaming? (and (= :preparing phase) (not generic-working?))
        running? (= :running phase)
        inspectable? (contains? #{:running :succeeded :failed :rejected :deferred :postponed} phase)
        label (activity-label props)
        row-attrs {:class ["nosco-chat-tool-activity"
                           (when (contains? #{:rejected :deferred :postponed} phase)
                             "nosco-chat-tool-activity--rejected")]
                   :data-tool-phase (name phase)}
        row-children [(when generic-working?
                        [:span {:class "nosco-chat-pending-dot" :aria-hidden true}])
                      [text/Text {:el :span :variant text/Body-Sm-Regular
                                  :class "nosco-chat-tool-activity__label"}
                       label]
                      (when definition-streaming?
                        [:span {:class "nosco-chat-pending-dot" :aria-hidden true}])
                      (when running?
                        [ToolRunningIndicator])]
        row-children (remove nil? row-children)]
    (if inspectable?
      [:details {:class "nosco-chat-tool-activity-disclosure"
                 :data-tool-phase (name phase)}
       (into [:summary row-attrs] row-children)
       [:div {:class "nosco-chat-tool-activity__details"}
        [text/Text {:el :div :variant text/Body-Sm-Regular
                    :class "nosco-chat-tool-activity__full-label"}
         label]
        (when (number? tokens)
          [text/Text {:el :div :variant text/Body-Sm-Regular
                      :class "nosco-chat-tool-activity__tokens"}
           (t/t "≈%s tokens sent to the model" tokens)])
        (when-let [details-text (payload->text details)]
          [text/Text {:el :pre :variant text/Body-Sm-Regular
                      :class "nosco-chat-tool-activity__raw"}
           details-text])]]
      (into [:div row-attrs] row-children))))

(declare ChatActionButton)

(defnc SingleChoiceRow
  "One single-select choice (Figma AskUserItem): an indicator (its number, or
   an icon) and the label; tinted with a selected border when chosen."
  [{:keys [id indicator label selected? disabled on-pick]}]
  [:button {:type "button"
            :id id
            :role "radio"
            :aria-checked (boolean selected?)
            :disabled disabled
            :class ["button-reset" "nosco-chat-ask-user-surface__choice"
                    "nosco-chat-ask-user-surface__pick"
                    (when selected? "nosco-chat-ask-user-surface__pick--selected")]
            :on-click on-pick}
   [:span {:class "nosco-chat-ask-user-surface__indicator" :aria-hidden true} indicator]
   [text/Text {:el :span :variant text/Body-Sm-Regular} label]])

(defnc OtherAnswerInput [{:keys [value disabled on-change]}]
  [inputs/TextInput
   {:size :small
    :value value
    :disabled disabled
    :focus-on-mount? true
    :aria-label (t/t "Other answer")
    :placeholder (t/t "Please specify…")
    :on-change #(on-change (.. % -target -value))}])

(defnc AskUserChoiceSurface
  "Proposed blocking multiple-choice question over one canonical ask_user call.
   Selection is transient form state; the session owns the tool answer."
  [{:keys [question choices max-selections allow-other? preparing? tool-call-id on-answer on-stop]}]
  (let [[selected-ids set-selected-ids!] (hooks/useState #{})
        [other-selected? set-other-selected!] (hooks/useState false)
        [other-text set-other-text!] (hooks/useState "")
        [submitting? set-submitting!] (hooks/useState false)
        [action-error set-action-error!] (hooks/useState nil)
        section-ref (React/useRef nil)
        option-ids (mapv :id choices)
        valid-choices?
        (and (vector? choices)
             (<= 2 (count choices) 8)
             (every? #(and (nonblank (:id %)) (nonblank (:label %))) choices)
             (= (count option-ids) (count (distinct option-ids)))
             (or (nil? max-selections) (pos-int? max-selections)))
        _ (when (and (not preparing?) (not valid-choices?))
            (throw (js/Error. "Invalid ask_user choices reached the question UI.")))
        single-answer? (= 1 max-selections)
        selection-count (+ (count selected-ids) (if other-selected? 1 0))
        at-limit? (and (pos-int? max-selections)
                       (>= selection-count max-selections))
        toggle! (fn [id]
                  (set-selected-ids!
                   (fn [selected]
                     (cond
                       (contains? selected id) (disj selected id)
                       (and (pos-int? max-selections)
                            (>= (+ (count selected)
                                   (if other-selected? 1 0))
                                max-selections)) selected
                       :else (conj selected id)))))
        other-answer (nonblank (string/trim other-text))
        answer {:selected-choice-ids
                (->> choices
                     (filter #(contains? selected-ids (:id %)))
                     (mapv :id))
                :other-text (when other-selected? other-answer)}
        valid-answer? (and (not preparing?) valid-choices?
                           (or (seq selected-ids) other-selected?)
                           (or (not other-selected?) other-answer))
        submit! (fn []
                  (when valid-answer?
                    (run-action! set-submitting! set-action-error!
                                 #(on-answer tool-call-id answer))))]
    ;; The card takes the composer's place, and its focus, so the number keys
    ;; work straight away.
    (hooks/useEffect
     (fn []
       (when-not preparing?
         (some-> (.-current section-ref) (.focus #js {:preventScroll true}))))
     [preparing?])
    [:section {:class "nosco-chat-ask-user-surface"
               :ref section-ref
               :tab-index -1
               :aria-label (t/t "Answer question")
               :on-key-down (fn [event]
                              (let [n (parse-long (.-key event))
                                    typing? (#{"INPUT" "TEXTAREA"} (.. event -target -tagName))]
                                (when (and single-answer? (not typing?) (not submitting?)
                                           n (<= 1 n (count choices)))
                                  (.preventDefault event)
                                  (set-selected-ids! #{(:id (nth choices (dec n)))})
                                  (set-other-selected! false))))}
     [text/Text {:el :div :variant text/Body-Sm-Regular
                 :class "nosco-chat-ask-user-surface__question"}
      (or (nonblank question) (t/t "Please choose an answer"))]
     (if preparing?
       [:div {:class "nosco-chat-ask-user-surface__choices"}
        [ActivityIndicator {:label (t/t "Preparing question")}]]
       (if single-answer?
         ;; Figma AskUserCard, single select: numbered rows (the number keys
         ;; pick them) and Something else… for a written answer.
         [:div {:class "nosco-chat-ask-user-surface__choices"
                :role "radiogroup"
                :aria-label (or (nonblank question) (t/t "Please choose an answer"))}
          (for [[index {:keys [id label]}] (map-indexed vector choices)]
            [SingleChoiceRow {:key id
                              :id (str "ask-user-" tool-call-id "-" id)
                              :indicator (str (inc index))
                              :label label
                              :selected? (contains? selected-ids id)
                              :disabled submitting?
                              :on-pick #(do (set-selected-ids! #{id})
                                            (set-other-selected! false))}])
          (when allow-other?
            [SingleChoiceRow {:id (str "ask-user-" tool-call-id "-other")
                              :indicator [icons2/Icon {:icon icons2/Create-xsmall :aria-hidden true}]
                              :label (t/t "Something else…")
                              :selected? other-selected?
                              :disabled submitting?
                              :on-pick #(do (set-selected-ids! #{})
                                            (set-other-selected! true))}])
          (when other-selected?
            [OtherAnswerInput {:value other-text
                               :disabled submitting?
                               :on-change set-other-text!}])]
         [:div {:class "nosco-chat-ask-user-surface__choices"}
          (for [{:keys [id label]} choices]
            [checkbox/CheckboxWithLabel
             {:key id
              :id (str "ask-user-" tool-call-id "-" id)
              :variant :small
              :label label
              :aria-label label
              :wrap-label? true
              :checked (contains? selected-ids id)
              :disabled submitting?
              :container-props {:class "nosco-chat-ask-user-surface__choice"}
              :on-change #(toggle! id)}])
          (when allow-other?
            [:div {:class "nosco-chat-ask-user-surface__other"}
             [checkbox/CheckboxWithLabel
              {:id (str "ask-user-" tool-call-id "-other")
               :variant :small
               :label (t/t "Something else…")
               :aria-label (t/t "Something else…")
               :wrap-label? true
               :checked other-selected?
               :disabled (or submitting? (and at-limit? (not other-selected?)))
               :container-props {:class "nosco-chat-ask-user-surface__choice"}
               :on-change #(set-other-selected! (not other-selected?))}]
             (when other-selected?
               [OtherAnswerInput {:value other-text
                                  :disabled submitting?
                                  :on-change set-other-text!}])])]))
     [:div {:class "nosco-chat-ask-user-surface__decisions"}
      [ChatActionButton {:action :stop
                         :disabled submitting?
                         :on-click on-stop}]
      [buttons2/Button {:variant buttons2/NewDefault
                        :size buttons2/SizeSmall
                        :label (t/t "Skip")
                        :class "nosco-chat-ask-user-surface__skip"
                        :disabled (or submitting? preparing?)
                        :on-click #(run-action! set-submitting! set-action-error!
                                                (fn [] (on-answer tool-call-id
                                                                  {:selected-choice-ids []
                                                                   :other-text nil
                                                                   :skipped? true})))}]
      [buttons2/Button {:variant buttons2/NewPrimary
                        :size buttons2/SizeSmall
                        :icon icons2/ArrowUp-small
                        :class "nosco-chat-ask-user-surface__send"
                        :aria-label (if submitting? (t/t "Sending…") (t/t "Send answer"))
                        :title (t/t "Send answer")
                        :disabled (or submitting? (not valid-answer?))
                        :on-click submit!}]]
     (when action-error
       [text/Text {:el :div :variant text/Body-Sm-Regular
                   :class "nosco-chat-card__action-error"}
        action-error])]))

(defn- approval-action [part]
  (let [tool-name (getToolName part)
        input (payload->clj (.-input part))]
    {:id (.-toolCallId part)
     :title (or (nonblank (tool-bridge/proposed-description input))
                (tool-catalogue/tool-human-label tool-name)
                (t/t "Proposed action"))
     :tool-name tool-name
     :arguments (tool-bridge/strip-presentation-input input)}))

(defnc ApprovalActionRow
  "One proposed action: its effect, a checkbox when it is one of several, and
   a chevron that reveals the tool and its arguments."
  [{:keys [action selected? on-toggle single-action?]}]
  (let [[expanded? set-expanded!] (hooks/useState false)
        {:keys [title tool-name arguments]} action]
    [:div {:class "nosco-chat-approval-surface__action"}
     [:div {:class "nosco-chat-approval-surface__summary"}
      (if single-action?
        [text/Text {:el :span :variant text/Body-Sm-Regular
                    :class "nosco-chat-approval-surface__title"}
         title]
        [checkbox/CheckboxWithLabel {:variant :small
                                     :label title
                                     :checked selected?
                                     :aria-label title
                                     :container-props {:class "nosco-chat-approval-surface__title"}
                                     :on-change on-toggle}])
      [buttons2/Button {:variant buttons2/NewIcon
                        :size buttons2/SizeSmall
                        :icon icons2/ChevronRight-small
                        :class ["nosco-chat-approval-surface__expand"
                                (when expanded? "nosco-chat-approval-surface__expand--open")]
                        :aria-expanded expanded?
                        :aria-label (t/t "Show details")
                        :title (t/t "Show details")
                        :on-click #(set-expanded! (not expanded?))}]]
     [animations/Collapse2 {:show expanded?}
      [:div {:class "nosco-chat-approval-surface__raw"
             :aria-hidden (not expanded?)}
       [text/Text {:el :pre :variant text/Body-Sm-Regular
                   :class "nosco-chat-approval-surface__arguments"}
        (str tool-name "\n" (payload->text arguments))]]]]))

(defnc ChatActionButton
  "The composer's Send (primary ArrowUp) and Stop (default Stop) button, also
   used by the transient approval UI."
  [{:keys [action on-click disabled type]}]
  (let [{:keys [icon variant label]}
        (case action
          :send {:icon icons2/ArrowUp-small
                 :variant buttons2/NewPrimary
                 :label (t/t "Send")}
          :stop {:icon icons2/Stop-small
                 :variant buttons2/NewDefault
                 :label (t/t "Stop")})]
    [buttons2/Button {:variant variant
                      :size buttons2/SizeSmall
                      :icon icon
                      :class "nosco-chat-action-button"
                      :aria-label label
                      :title label
                      :type (or type "button")
                      :disabled disabled
                      :on-click on-click}]))

(defn- attachment-status-text [{:keys [status error]}]
  (case status
    :uploading (t/t "Uploading…")
    :reading (t/t "Reading…")
    :extracting (t/t "Reading…")
    :ready nil
    :failed (case error
              "file_too_large" (t/t "Too large, max 50 MB")
              "image_too_large" (t/t "Too large, max 3.75 MB")
              "image_dimensions_too_large" (t/t "Too large, max 8000 pixels per side")
              ("unsupported_type" "unsupported_mime" "not_extractable")
              (t/t "Unsupported file type")
              "extraction_timeout" (t/t "Took too long to read. Retry?")
              (t/t "Could not read. Retry?"))))

(defn- attachment-kind
  "PDF, JPEG, …: the file name's extension, else the media subtype."
  [{:keys [filename type]}]
  (some-> (or (second (re-find #"\.([A-Za-z0-9]+)$" (or filename "")))
              (second (re-find #"/([A-Za-z0-9]+)" (or type ""))))
          string/upper-case))

(defn- attachment-meta [{:keys [size truncated?] :as item}]
  (or (attachment-status-text item)
      (string/join " • " (remove nil? [(attachment-kind item)
                                       (when (number? size) (progress/human-readable-filesize size))
                                       (when truncated? (t/t "Truncated"))]))))

(defnc AttachmentCards
  "Pending composer attachments (`nosco.ai.attachments`): a thumbnail or file
   icon, the name and type/size or status. Each can be removed until the
   message is sent; a failed one retries on click."
  [{:keys [items on-remove on-retry]}]
  (when (seq items)
    [:div {:class "nosco-chat-panel__attachments"}
     (for [{:keys [id status filename url] :as item} items]
       (let [failed? (= :failed status)
             content [:<>
                      [:span {:class "nosco-chat-attachment__thumb"}
                       (cond
                         url [:img {:class "nosco-chat-image" :src url :alt (t/t "Attached image")}]
                         failed? [icons2/Icon {:icon icons2/SemanticWarning-small :aria-hidden true}]
                         :else [icons2/Icon {:icon icons2/File-small :aria-hidden true}])]
                      [:span {:class "nosco-chat-attachment__text"}
                       [text/Text {:el :span :variant text/Body-Sm-Regular
                                   :class "nosco-chat-attachment__name"}
                        filename]
                       [:span {:class "nosco-chat-attachment__meta"} (attachment-meta item)]]]]
         [:div {:key id
                :class ["nosco-chat-attachment" (when failed? "nosco-chat-attachment--failed")]}
          (if failed?
            [:button {:type "button"
                      :class "button-reset nosco-chat-attachment__body"
                      :title (t/t "Retry")
                      :on-click #(on-retry id)}
             content]
            [:span {:class "nosco-chat-attachment__body"} content])
          [buttons2/Button {:variant buttons2/NewIcon
                            :size buttons2/SizeSmall
                            :icon icons2/CloseSmall-small
                            :aria-label (t/t "Remove %s" filename)
                            :title (t/t "Remove")
                            :on-click #(on-remove id)}]]))]))

(defnc SentAttachments
  "The files a sent user message carried (its `attachments` metadata), as
   tiles: the image itself, or a file icon named in its tooltip."
  [{items :attachments}]
  [:div {:class "nosco-chat-msg__attachments"}
   (for [{:keys [id url filename]} items]
     [:span {:key id :class "nosco-chat-msg__attachment" :title filename}
      (if url
        [:img {:class "nosco-chat-image" :src url :alt (t/t "Attached image")}]
        [icons2/Icon {:icon icons2/File-small :aria-label filename}])])])

(defnc ChatComposerTools
  "The toolbar's left slot: Attach (only with `on-attach-files`, i.e. the
   `gamma-media` flag on)."
  [{:keys [on-attach-files accept]}]
  (let [input-ref (React/useRef nil)]
    [:div {:class "nosco-chat-panel__composer-tools"}
     (when on-attach-files
       [:<>
        [:input {:ref input-ref
                 :type "file"
                 :multiple true
                 :accept accept
                 :hidden true
                 :on-change (fn [event]
                              (let [input (.-target event)]
                                (on-attach-files (array-seq (.-files input)))
                                ;; so choosing the same file again fires a change
                                (set! (.-value input) "")))}]
        [buttons2/Button {:variant buttons2/NewIcon
                          :size buttons2/SizeSmall
                          :icon icons2/Attach-small
                          :aria-label (t/t "Attach files")
                          :title (t/t "Attach files")
                          :on-click #(some-> (.-current input-ref) .click)}]])]))

(defnc ApprovalSurface
  "One transient current-step decision surface. Selected IDs are ephemeral
   checkbox input; the caller/session remains owner of every SDK response."
  [{:keys [parts preparing? summary-title summary-justification
           on-approve-all on-selected on-reject-all on-stop]}]
  (let [[selected-ids set-selected-ids!] (hooks/useState #{})
        [submitting? set-submitting!] (hooks/useState false)
        [action-error set-action-error!] (hooks/useState nil)
        actions (mapv approval-action parts)
        single-action? (= 1 (count actions))
        decide! (fn [action!]
                  (run-action! set-submitting! set-action-error! action!))]
    [:section {:class "nosco-chat-approval-surface"
               :aria-label (t/t "Review proposed actions")}
     (when-not single-action?
       [text/Text {:el :div :variant text/Body-Sm-Regular
                   :class "nosco-chat-approval-surface__heading"}
        (or (when-not preparing? (nonblank summary-title))
            (if preparing?
              (t/t "Review proposed actions")
              (t/t "Review %s proposed actions" (count actions))))])
     (when-let [justification (when-not preparing?
                                (nonblank summary-justification))]
       [text/Text {:el :div :variant text/Body-Sm-Regular
                   :class "nosco-chat-approval-surface__justification"}
        justification])
     (when-not preparing?
       [:div {:class "nosco-chat-approval-surface__actions"}
        (for [{:keys [id] :as action} actions]
          [ApprovalActionRow
           {:key id
            :action action
            :single-action? single-action?
            :selected? (contains? selected-ids id)
            :on-toggle #(set-selected-ids!
                         (fn [selected]
                           (if (contains? selected id)
                             (disj selected id)
                             (conj selected id))))}])])
     [:div {:class "nosco-chat-approval-surface__decisions"}
      [ChatActionButton {:action :stop
                         :disabled submitting?
                         :on-click on-stop}]
      (if preparing?
        [ActivityIndicator {:label (t/t "Preparing actions")}]
        [:<>
         [buttons2/Button {:variant buttons2/NewDefault
                           :size buttons2/SizeSmall
                           :label (t/t "Decline")
                           :class "nosco-chat-approval-surface__decline"
                           :disabled submitting?
                           :on-click #(decide! on-reject-all)}]
         [buttons2/Button {:variant buttons2/NewPrimary
                           :size buttons2/SizeSmall
                           :class "nosco-chat-approval-surface__approve"
                           :label (if (empty? selected-ids)
                                    (if single-action?
                                      (t/t "Approve")
                                      (t/t "Approve all"))
                                    (t/t "Approve selected"))
                           :disabled submitting?
                           :on-click #(decide!
                                       (if (empty? selected-ids)
                                         on-approve-all
                                         (fn [] (on-selected selected-ids))))}]])]
     (when action-error
       [text/Text {:el :div :variant text/Body-Sm-Regular
                   :class "nosco-chat-card__action-error"}
        action-error])]))

(defn- part-view
  "How one tool part shows: nil (hidden), `[:navigation link-props]`,
   `[:preparing-navigation]`, or `[:activity ToolActivity-props]`."
  [part question-actionable?]
  (let [tool-name (getToolName part)
        presentation-input (payload->clj (.-input part))
        input (tool-bridge/strip-presentation-input presentation-input)
        output (payload->clj (.-output part))
        ask-user? (= "ask_user" tool-name)
        navigation? (= "suggest_navigation" tool-name)
        resource? (= :resource (tool-catalogue/tool-category tool-name))]
    (cond
      (and ask-user? question-actionable?)
      nil

      (and navigation? (= "output-available" (.-state part))
           (true? (:ok output)))
      [:navigation (get-in output [:raw :data :navigation])]

      navigation?
      (when (contains? #{"input-streaming" "input-available"} (.-state part))
        [:preparing-navigation])

      ;; Not run by the context-budget gate; the model calls it again in its
      ;; next step or once the conversation is summarised. Not an error, not a
      ;; user rejection.
      (and (= "output-denied" (.-state part))
           (contains? #{context-budget/deferred-reason context-budget/postponed-reason}
                      (some-> part .-approval .-reason)))
      (let [reason (.. part -approval -reason)]
        [:activity
         {:phase (if (= context-budget/postponed-reason reason) :postponed :deferred)
          :description (tool-bridge/activity-description presentation-input)
          :details {:input input :reason reason}}])

      (or resource? ask-user?)
      (let [phase (case (.-state part)
                    "input-streaming" :preparing
                    "input-available" :running
                    "output-available" (if (false? (:ok output)) :failed :succeeded)
                    "output-error" :failed
                    "output-denied" :failed
                    :preparing)
            error (case (.-state part)
                    "output-error" (.-errorText part)
                    "output-denied" (some-> part .-approval .-reason)
                    (:error output))
            details (case phase
                      :running input
                      ;; A saved result keeps only its file name in the
                      ;; transcript; show the input, then what the model
                      ;; received as plain text so its line breaks survive.
                      :succeeded (if (get-in output [:raw :savedAs])
                                   (str (payload->text {:input input})
                                        "\n\n" (:modelText output))
                                   {:input input
                                    :output (or (:raw output) output)})
                      :failed {:input input :error error}
                      nil)]
        [:activity
         {:phase phase
          :description (tool-bridge/activity-description presentation-input)
          :completed-description (tool-bridge/completed-description presentation-input)
          :details details
          :tokens (:modelTokens output)}])
      :else
      (when-not (or (= "approval-requested" (.-state part))
                    (vercel-chat/write-tool-preparing? part))
        (let [phase (case (.-state part)
                      "input-streaming" :preparing
                      "approval-responded" (if (false? (some-> part .-approval .-approved))
                                             :rejected :preparing)
                      "input-available" :running
                      "output-available" (if (false? (:ok output)) :failed :succeeded)
                      "output-error" :failed
                      "output-denied" :rejected)
              raw {:tool tool-name :arguments input}
              details (case phase
                        :running raw
                        :succeeded (assoc raw :output (or (:raw output) output))
                        :failed (assoc raw :error (or (:error output)
                                                      (.-errorText part)))
                        :rejected raw
                        nil)]
          [:activity
           {:phase phase
            :description (tool-bridge/activity-description presentation-input)
            :completed-description (tool-bridge/completed-description presentation-input)
            :proposed-description (tool-bridge/proposed-description presentation-input)
            :details details
            :tokens (:modelTokens output)}])))))

(defnc NavigationLink [{:keys [href label]}]
  [buttons2/Link {:variant buttons2/NewDefault
                  :size buttons2/SizeSmall
                  :icon icons2/Open-small
                  :class "nosco-chat-navigation"
                  :href href
                  :label label}])

(defnc ToolPartCard
  [{:keys [part question-actionable?]}]
  (let [[kind props] (part-view part question-actionable?)]
    (case kind
      :navigation [NavigationLink props]
      :preparing-navigation [ActivityIndicator {:label (t/t "Preparing navigation…")}]
      :activity [ToolActivity props]
      nil)))

(def ^:private active-phases #{:preparing :running})

(defnc ActivityGroup
  "A turn's consecutive tool steps as one line. While a step is working it
   shows that step with a dot (steps replace each other, they don't stack);
   once all are settled it shows the latest step, opening onto every step.
   `steps` is a seq of `[key ToolActivity-props]`."
  [{:keys [steps]}]
  (let [latest (second (last steps))
        active (some->> steps (filter #(active-phases (:phase (second %)))) last second)]
    (if active
      [:div {:class "nosco-chat-tool-activity nosco-chat-activity-group__active"
             :role "status"
             :aria-label (activity-label active)
             :data-tool-phase (name (:phase active))}
       [:span {:class "nosco-chat-pending-dot" :aria-hidden true}]
       [text/Text {:el :span :variant text/Body-Sm-Regular
                   :class "nosco-chat-tool-activity__label"}
        (activity-label active)]]
      [:details {:class ["nosco-chat-activity-group"
                         (when (= :failed (:phase latest)) "nosco-chat-activity-group--failed")]}
       [:summary {:class "nosco-chat-activity-group__summary"}
        [text/Text {:el :span :variant text/Body-Sm-Regular
                    :class "nosco-chat-activity-group__label"}
         (activity-label latest)]
        [icons2/Icon {:icon icons2/ChevronRight-small
                      :class "nosco-chat-activity-group__chevron"
                      :aria-hidden true}]]
       [:ul {:class "nosco-chat-activity-group__steps"}
        (for [[k props] steps]
          [:li {:key k :class "nosco-chat-activity-group__step"}
           [ToolActivity props]])]])))

(defn- segments
  "The message's parts in order as `[:text index part]`, `[:activities steps]`
   (consecutive tool steps; reasoning between them does not split a group),
   `[:navigation index props]` and `[:preparing-navigation index]`."
  [parts pending-question-call-ids]
  (reduce
   (fn [acc [index part]]
     (let [part-type (.-type part)]
       (cond
         (= "text" part-type) (conj acc [:text index part])
         (= "reasoning" part-type) acc
         (isToolUIPart part)
         (let [[kind props] (part-view part (contains? pending-question-call-ids
                                                       (.-toolCallId part)))
               step-key (str "tool-" index "-" (or (.-toolCallId part) ""))]
           (case kind
             :activity (if (= :activities (first (peek acc)))
                         (conj (pop acc) (update (peek acc) 1 conj [step-key props]))
                         (conj acc [:activities [[step-key props]]]))
             :navigation (conj acc [:navigation index props])
             :preparing-navigation (conj acc [:preparing-navigation index])
             acc))
         :else acc)))
   []
   (map-indexed vector parts)))

(defnc AssistantMessage
  [{:keys [message streaming? pending-question-call-ids]}]
  [:div {:class ["nosco-chat-msg" "nosco-chat-msg--assistant"]}
   (for [[kind a b] (segments (array-seq (.-parts message)) pending-question-call-ids)]
     (case kind
       :text [MarkdownBody {:key (str "text-" a "-" (or (.-id b) ""))
                            :content (.-text b)
                            :streaming? (and streaming? (not= "done" (.-state b)))}]
       :activities [ActivityGroup {:key (first (first a)) :steps a}]
       :navigation [NavigationLink (assoc b :key (str "nav-" a))]
       :preparing-navigation [ActivityIndicator {:key (str "nav-" a)
                                                 :label (t/t "Preparing navigation…")}]))])

(defnc CopyButton [{:keys [label value icon-only? class render-as on-close variant]}]
  (let [[state set-state!] (hooks/useState :ready)
        feedback-label (case state
                         :copied (t/t "Copied")
                         :failed (t/t "Copy failed")
                         label)
        copy! (fn []
                (let [write (.writeText js/navigator.clipboard value)]
                  (when on-close (on-close))
                  (-> write
                      (.then (fn []
                               (when-not (= render-as :menu)
                                 (set-state! :copied)
                                 (js/setTimeout #(set-state! :ready) 1500))))
                      (.catch (fn [error]
                                (when-not (= render-as :menu)
                                  (set-state! :failed))
                                (js/console.error "AI Chat clipboard write failed" error))))))]
    (if (= render-as :menu)
      [menus/MenuItem {:el :button
                       :label feedback-label
                       :class class
                       :disabled (string/blank? value)
                       :on-click copy!}]
      [buttons2/Button
       {:variant (or variant buttons2/NewTextSubtle)
        :size buttons2/SizeSmall
        :class class
        :icon (when icon-only? icons2/DocumentCopy-small)
        :label (when-not icon-only? feedback-label)
        :aria-label feedback-label
        :title feedback-label
        :disabled (string/blank? value)
        :on-click copy!}])))

(defnc SectionMessage
  "A notice in the conversation (Figma SectionMessage): `:error` for a failed
   request, `:info` for an interrupted answer, with optional `actions`."
  [{:keys [variant children actions]}]
  [:div {:class ["nosco-chat-section-message"
                 (str "nosco-chat-section-message--" (name variant))]
         :role (when (= :error variant) "alert")}
   [icons2/Icon {:icon (if (= :error variant) icons2/SemanticError-large icons2/Info-large)
                 :class "nosco-chat-section-message__icon"
                 :aria-hidden true}]
   [:div {:class "nosco-chat-section-message__body"}
    [text/Text {:el :div :variant text/Body-Md-Regular
                :class "nosco-chat-section-message__text"}
     children]
    (when actions
      [:div {:class "nosco-chat-section-message__actions"} actions])]])

(defn message-time
  "When the message was sent or finished, as \"12 minutes ago\"; nil for
   messages saved before the time was recorded."
  [iso]
  (when (nonblank iso)
    (nosco-format/time-ago (js/Date. iso) {:numeric-style "always"
                                           :fallback-style :medium-date})))

(defnc MessageTime
  "When a message was sent or finished, kept current: it re-renders itself
   every minute, since settled messages otherwise never re-render."
  [{:keys [iso class title]}]
  (let [[_ set-tick!] (hooks/useState 0)]
    (hooks/useEffect
     (fn []
       (let [timer (js/setInterval #(set-tick! inc) 60000)]
         #(js/clearInterval timer)))
     [])
    (when-let [label (message-time iso)]
      [text/Text {:el :span :variant text/Body-Sm-Regular :class class :title title}
       label])))

;; Hidden for now (Orestis, 2026-10-22): the Figma answer row has Retry, but
;; it's not clear what it adds over asking again. Raised with René.
(def show-retry? false)

(defnc AssistantActionMetadata
  "Quiet per-message actions over canonical finish metadata, for answers with
   text: copy, Retry (given `on-retry`), the model that answered and when the answer finished. Duration and usage, when
   the session/provider supplied them, are in the time's tooltip."
  [{:keys [message on-retry]}]
  (let [metadata (payload->clj (.-metadata message))
        usage (:usage metadata)
        duration-ms (:durationMs metadata)
        input-tokens (:inputTokens usage)
        output-tokens (:outputTokens usage)
        cache-read (get-in usage [:inputTokenDetails :cacheReadTokens])
        parts (cond-> []
                (number? duration-ms)
                (conj (t/t "%ss" (quot (+ duration-ms 500) 1000)))
                (number? input-tokens)
                (conj (t/t "%s in" input-tokens))
                (and (number? input-tokens) (pos? input-tokens)
                     (number? cache-read) (pos? cache-read))
                (conj (t/t "%s cached"
                           (str (min 99 (js/Math.floor (* 100 (/ cache-read input-tokens)))) "%")))
                (number? output-tokens)
                (conj (t/t "%s out" output-tokens)))
        usage-text (when (seq parts) (string/join " · " parts))
        finished-at (:finishedAt metadata)
        model (:model metadata)
        prose (vercel-chat/raw-message-text message)]
    ;; Only an answer with text has the row; a turn of tool steps alone
    ;; (say, waiting for approval) does not.
    (when-not (string/blank? prose)
      [:div {:class "nosco-chat-msg__action-metadata"}
       [CopyButton {:label (t/t "Copy message")
                    :value prose
                    :icon-only? true
                    :variant buttons2/NewIcon
                    :class "nosco-chat-msg__assistant-copy"}]
       (when (and show-retry? on-retry)
         [buttons2/Button {:variant buttons2/NewIcon
                           :size buttons2/SizeSmall
                           :icon icons2/Undo2-small
                           :class "nosco-chat-msg__retry"
                           :aria-label (t/t "Retry")
                           :title (t/t "Retry")
                           :on-click on-retry}])
       (when model
         [text/Text {:el :span :variant text/Body-Sm-Regular
                     :class "nosco-chat-msg__action-metadata-text"}
          model])
       (if (message-time finished-at)
         [MessageTime {:iso finished-at
                       :class "nosco-chat-msg__action-metadata-text"
                       :title usage-text}]
         (when usage-text
           [text/Text {:el :span :variant text/Body-Sm-Regular
                       :class "nosco-chat-msg__action-metadata-text"}
            usage-text]))])))
